import React, { useEffect, useState } from "react";
import {
  collection, addDoc, getDocs, doc,
  updateDoc, Timestamp, orderBy, query,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast, ToastContainer } from "react-toastify";
import "../css/datapanel.css";
import "../css/collection.css";

const COLLECTION_TYPES = [
  "Zakat", "Fitrah", "Mustahab Sadqah", "Sehme Imam",
  "Sehme Saadat", "Fidya", "Kaffara", "Akiqah",
  "Donation", "Custom",
];

const VOLUNTEERS = [
  "Ahmad", "Zain", "Abbas Mirza", "Ali Mohammad",
  "Hussain", "Mahdi", "Arsalan", "Ali Mehdi", "Ali Abbas",
];

// ── Helper: returns customSubType for Custom entries, otherwise type ──────────
const getEffectiveType = (c) =>
  c.type === "Custom" && c.customSubType ? c.customSubType : (c.type || "");

// ── Delete Modal ──────────────────────────────────────────────
function DeleteModal({ entry, onConfirm, onCancel }) {
  const [note, setNote] = useState("");
  const [recipient, setRecipient] = useState("");
  return (
    <div className="cl-modal-bg" onClick={onCancel}>
      <div className="cl-modal" onClick={e => e.stopPropagation()}>
        <h3 className="cl-modal-title">🗑 Delete Entry</h3>
        <p className="cl-modal-desc">
          Deleting <strong>₹{entry.amount} — {getEffectiveType(entry)}</strong>.<br />
          This will be moved to deleted records. Please add a note about who received this amount.
        </p>
        <div className="cl-modal-field">
          <label>Recipient / Who Got Helped *</label>
          <input
            value={recipient}
            onChange={e => setRecipient(e.target.value)}
            placeholder="e.g. Mohammed Ali, Raza family"
          />
        </div>
        <div className="cl-modal-field">
          <label>Note (reason for deletion / details)</label>
          <textarea
            rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. Amount given to beneficiary for medical treatment…"
          />
        </div>
        <div className="cl-modal-actions">
          <button className="dp-cancel-btn" onClick={onCancel}>Cancel</button>
          <button
            className="cl-delete-confirm-btn"
            onClick={() => {
              if (!recipient.trim()) { toast.error("Recipient is required."); return; }
              onConfirm(note, recipient);
            }}
          >
            Confirm Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────────
function EditModal({ entry, onConfirm, onCancel }) {
  const [amount,    setAmount]    = useState(entry.amount);
  const [type,      setType]      = useState(entry.type);
  const [notes,     setNotes]     = useState(entry.notes || "");
  const [editNote,  setEditNote]  = useState("");
  const [recipient, setRecipient] = useState("");

  return (
    <div className="cl-modal-bg" onClick={onCancel}>
      <div className="cl-modal" onClick={e => e.stopPropagation()}>
        <h3 className="cl-modal-title">✏️ Edit Entry</h3>
        <p className="cl-modal-desc">
          Original: <strong>₹{entry.amount} — {getEffectiveType(entry)}</strong>
        </p>
        <div className="cl-modal-grid">
          <div className="cl-modal-field">
            <label>New Amount (₹) *</label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
            />
          </div>
          <div className="cl-modal-field">
            <label>Type</label>
            <select value={type} onChange={e => setType(e.target.value)}>
              {COLLECTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="cl-modal-field">
          <label>Entry Notes</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes…" />
        </div>
        <div className="cl-modal-field">
          <label>Who received the difference / reason for edit *</label>
          <input
            value={recipient}
            onChange={e => setRecipient(e.target.value)}
            placeholder="e.g. Hussain family received ₹500"
          />
        </div>
        <div className="cl-modal-field">
          <label>Edit Note</label>
          <textarea
            rows={2}
            value={editNote}
            onChange={e => setEditNote(e.target.value)}
            placeholder="Describe what changed and why…"
          />
        </div>
        <div className="cl-modal-actions">
          <button className="dp-cancel-btn" onClick={onCancel}>Cancel</button>
          <button
            className="dp-save-btn"
            onClick={() => {
              if (!amount || !recipient.trim()) { toast.error("Amount and recipient are required."); return; }
              onConfirm({ amount, type, notes, editNote, recipient });
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Minus Entry Modal ─────────────────────────────────────────
function MinusEntryModal({ collections, onConfirm, onCancel }) {
  const [amount,     setAmount]     = useState("");
  const [type,       setType]       = useState("");
  const [customSub,  setCustomSub]  = useState(""); // only used when type === "Custom"
  const [removingBy, setRemovingBy] = useState("");
  const [notes,      setNotes]      = useState("");

  // Only show types that actually have entries (non-custom)
  const availableNonCustomTypes = [...new Set(
    collections.filter(c => c.type !== "Custom").map(c => c.type).filter(Boolean)
  )];

  // All distinct custom sub-types that exist in collections
  const customSubTypes = [...new Set(
    collections
      .filter(c => c.type === "Custom" && c.customSubType)
      .map(c => c.customSubType)
  )];

  const hasCustomEntries = customSubTypes.length > 0;

  // The human-readable label for the current selection
  const effectiveLabel = type === "Custom" ? customSub : type;

  // Available balance for the current selection
  const availableForType = (() => {
    if (!type) return 0;
    if (type === "Custom") {
      if (!customSub) return 0;
      return collections
        .filter(c => c.type === "Custom" && c.customSubType === customSub)
        .reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    }
    return collections
      .filter(c => c.type === type)
      .reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  })();

  const showBanner = type && (type !== "Custom" || customSub);
  const parsedAmount = parseFloat(amount);
  const isOver = showBanner && amount && parsedAmount > availableForType;

  const handleConfirm = () => {
    if (!amount || isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Enter a valid amount greater than 0."); return;
    }
    if (!type) { toast.error("Please select a type."); return; }
    if (type === "Custom" && !customSub) {
      toast.error("Please select a custom sub-type."); return;
    }
    if (availableForType === 0) {
      toast.error(`No entries found for "${effectiveLabel}".`); return;
    }
    if (parsedAmount > availableForType) {
      toast.error(
        `Available in "${effectiveLabel}" is ₹${availableForType.toLocaleString("en-IN")}. Please enter ₹${availableForType.toLocaleString("en-IN")} or less.`
      );
      return;
    }
    // effectiveType is what's used to match entries in the parent handler
    const effectiveType = type === "Custom" ? customSub : type;
    onConfirm({ amount: parsedAmount, effectiveType, removingBy, notes });
  };

  return (
    <div className="cl-modal-bg" onClick={onCancel}>
      <div className="cl-modal" onClick={e => e.stopPropagation()}>
        <h3 className="cl-modal-title">➖ Minus Entry</h3>
        <p className="cl-modal-desc">
          Deduct an amount from the <strong>oldest entries first</strong> of the selected type.
          Fully consumed entries are moved to deleted records automatically.
        </p>

        <div className="cl-modal-grid">
          <div className="cl-modal-field">
            <label>Type *</label>
            <select
              value={type}
              onChange={e => { setType(e.target.value); setCustomSub(""); }}
            >
              <option value="">Select type</option>
              {availableNonCustomTypes.map(t => <option key={t} value={t}>{t}</option>)}
              {hasCustomEntries && <option value="Custom">Custom</option>}
            </select>
          </div>
          <div className="cl-modal-field">
            <label>Amount (₹) *</label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="Enter amount"
              min="1"
            />
          </div>
        </div>

        {/* Custom sub-type dropdown — appears only when Custom is selected */}
        {type === "Custom" && (
          <div className="cl-modal-field">
            <label>Custom Sub-Type *</label>
            <select value={customSub} onChange={e => setCustomSub(e.target.value)}>
              <option value="">Select custom type</option>
              {customSubTypes.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
        )}

        {/* Live available balance banner */}
        {showBanner && (
          <div className={`cl-minus-available${isOver ? " cl-minus-over" : ""}`}>
            {isOver
              ? `⚠️ Exceeds available! Only ₹${availableForType.toLocaleString("en-IN")} available in "${effectiveLabel}"`
              : `✅ Available in "${effectiveLabel}": ₹${availableForType.toLocaleString("en-IN")}`
            }
          </div>
        )}

        <div className="cl-modal-field">
          <label>Removing By</label>
          <select value={removingBy} onChange={e => setRemovingBy(e.target.value)}>
            <option value="">Select volunteer</option>
            {VOLUNTEERS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        <div className="cl-modal-field">
          <label>Notes</label>
          <textarea
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Reason for deduction, recipient details…"
          />
        </div>

        <div className="cl-modal-actions">
          <button className="dp-cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="cl-minus-confirm-btn" onClick={handleConfirm}>
            Deduct Amount
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
const Collection = () => {
  const [collections,    setCollections]    = useState([]);
  const [deleted,        setDeleted]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [showForm,       setShowForm]       = useState(false);
  const [showDeleted,    setShowDeleted]    = useState(false);
  const [search,         setSearch]         = useState("");
  const [typeFilter,     setTypeFilter]     = useState("all");
  const [sortOrder,      setSortOrder]      = useState("desc");
  const [expanded,       setExpanded]       = useState(null);
  const [deleteTarget,   setDeleteTarget]   = useState(null);
  const [editTarget,     setEditTarget]     = useState(null);
  const [showMinusModal, setShowMinusModal] = useState(false);

  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    amount: "", type: "", notes: "", bookedBy: "",
    useCustomDate: false,
    customSubType: "", // ← NEW: only used when type === "Custom"
  });

  useEffect(() => { fetchAll(); }, [sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    try {
      const q    = query(collection(db, "Collection"), orderBy("date", sortOrder));
      const snap = await getDocs(q);
      // FIX: filter out soft-deleted entries so they don't count toward the total
      setCollections(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => !d.deleted)
      );

      const dq    = query(collection(db, "CollectionDeleted"), orderBy("deletedAt", "desc"));
      const dsnap = await getDocs(dq);
      setDeleted(dsnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { toast.error("Failed to load."); }
    setLoading(false);
  };

  // ── Add ──
  const handleSubmit = async () => {
    if (!form.amount.trim() || !form.type.trim()) {
      toast.error("Amount and type are required."); return;
    }
    // Validate custom sub-type name
    if (form.type === "Custom" && !form.customSubType.trim()) {
      toast.error("Please enter a name for the custom type."); return;
    }
    try {
      const payload = {
        date:          form.useCustomDate ? Timestamp.fromDate(new Date(form.date)) : Timestamp.now(),
        amount:        form.amount,
        type:          form.type,
        customSubType: form.type === "Custom" ? form.customSubType.trim() : "", // ← NEW
        notes:         form.notes,
        bookedBy:      form.bookedBy,
        history:       [],
      };
      const ref = await addDoc(collection(db, "Collection"), payload);
      setCollections(prev => [{ id: ref.id, ...payload }, ...prev]);
      toast.success("Entry added.");
      setForm({
        date: new Date().toISOString().split("T")[0],
        amount: "", type: "", notes: "", bookedBy: "",
        useCustomDate: false, customSubType: "",
      });
      setShowForm(false);
    } catch { toast.error("Failed to add."); }
  };

  // ── Delete → move to CollectionDeleted ──
  const handleDeleteConfirm = async (note, recipient) => {
    const entry = deleteTarget;
    try {
      await addDoc(collection(db, "CollectionDeleted"), {
        ...entry,
        deletedAt:         Timestamp.now(),
        deletionNote:      note,
        deletionRecipient: recipient,
      });
      await updateDoc(doc(db, "Collection", entry.id), { deleted: true, deletedAt: Timestamp.now() });
      // FIX: remove from local state immediately so total updates at once
      setCollections(prev => prev.filter(c => c.id !== entry.id));
      toast.success("Entry moved to deleted records.");
    } catch { toast.error("Delete failed."); }
    setDeleteTarget(null);
    fetchAll();
  };

  // ── Edit ──
  const handleEditConfirm = async ({ amount, type, notes, editNote, recipient }) => {
    const entry = editTarget;
    const prevHistory = entry.history || [];
    const historyEntry = {
      at:         Timestamp.now(),
      prevAmount: entry.amount,
      newAmount:  amount,
      prevType:   entry.type,
      newType:    type,
      editNote,
      recipient,
    };
    try {
      await updateDoc(doc(db, "Collection", entry.id), {
        amount,
        type,
        notes,
        history:      [...prevHistory, historyEntry],
        lastEditedAt: Timestamp.now(),
      });
      setCollections(prev => prev.map(c =>
        c.id === entry.id
          ? { ...c, amount, type, notes, history: [...prevHistory, historyEntry] }
          : c
      ));
      toast.success("Entry updated.");
    } catch { toast.error("Edit failed."); }
    setEditTarget(null);
  };

  // ── Minus Entry ──────────────────────────────────────────────
  const handleMinusConfirm = async ({ amount, effectiveType, removingBy, notes }) => {
    // Match entries by effective type (custom sub-type or regular type)
    const typeEntries = collections
      .filter(c => getEffectiveType(c) === effectiveType)
      .sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
        return dateA - dateB; // oldest first
      });

    const totalAvailable = typeEntries.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

    // Safety re-check
    if (amount > totalAvailable) {
      toast.error(
        `Available in "${effectiveType}" is ₹${totalAvailable.toLocaleString("en-IN")}. Cannot deduct ₹${amount.toLocaleString("en-IN")}.`
      );
      return;
    }

    let remaining = amount;

    try {
      for (const entry of typeEntries) {
        if (remaining <= 0) break;

        const entryAmount = parseFloat(entry.amount) || 0;

        if (entryAmount <= remaining) {
          // Fully consumed → move to CollectionDeleted
          remaining = parseFloat((remaining - entryAmount).toFixed(2));
          await addDoc(collection(db, "CollectionDeleted"), {
            ...entry,
            deletedAt:         Timestamp.now(),
            deletionNote:      notes || "Minus entry deduction",
            deletionRecipient: removingBy || "—",
            deletionReason:    "minus-entry",
          });
          await updateDoc(doc(db, "Collection", entry.id), {
            deleted:   true,
            deletedAt: Timestamp.now(),
          });
        } else {
          // Partial deduction → update amount + add to edit history
          const newAmount = (entryAmount - remaining).toFixed(2);
          remaining = 0;
          const prevHistory = entry.history || [];
          const historyEntry = {
            at:         Timestamp.now(),
            prevAmount: String(entry.amount),
            newAmount,
            prevType:   entry.type,
            newType:    entry.type,
            editNote:   notes || "Minus entry deduction",
            recipient:  removingBy || "—",
          };
          await updateDoc(doc(db, "Collection", entry.id), {
            amount:       newAmount,
            history:      [...prevHistory, historyEntry],
            lastEditedAt: Timestamp.now(),
          });
        }
      }

      toast.success(`₹${amount.toLocaleString("en-IN")} deducted from "${effectiveType}" successfully.`);
      setShowMinusModal(false);
      fetchAll();
    } catch (err) {
      console.error("Minus entry error:", err);
      toast.error("Deduction failed. Please try again.");
    }
  };

  // Use getEffectiveType for totals and filtering
  const totalAmount = collections
    .filter(c => typeFilter === "all" || getEffectiveType(c) === typeFilter)
    .reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);

  // Unique display types (custom sub-names replace "Custom")
  const uniqueTypes = [...new Set(collections.map(getEffectiveType).filter(Boolean))];

  const filtered = collections.filter(c => {
    if (typeFilter !== "all" && getEffectiveType(c) !== typeFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (
        !getEffectiveType(c).toLowerCase().includes(s) &&
        !(c.amount||"").toString().includes(s) &&
        !(c.notes||"").toLowerCase().includes(s) &&
        !(c.bookedBy||"").toLowerCase().includes(s)
      ) return false;
    }
    return true;
  });

  const fmt = ts => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
  };

  return (
    <div className="dp-root">
      <ToastContainer />

      {/* Delete Modal */}
      {deleteTarget && (
        <DeleteModal
          entry={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Edit Modal */}
      {editTarget && (
        <EditModal
          entry={editTarget}
          onConfirm={handleEditConfirm}
          onCancel={() => setEditTarget(null)}
        />
      )}

      {/* Minus Entry Modal */}
      {showMinusModal && (
        <MinusEntryModal
          collections={collections}
          onConfirm={handleMinusConfirm}
          onCancel={() => setShowMinusModal(false)}
        />
      )}

      {/* Header */}
      <div className="dp-header">
        <div className="dp-header-left">
          <span className="dp-header-icon">💰</span>
          <div>
            <h2 className="dp-title">Collection</h2>
            <p className="dp-subtitle">Track donations, zakat, and all financial collections</p>
          </div>
        </div>
        <div className="dp-header-btns">
          <button className="dp-refresh-btn" onClick={fetchAll}>↻ Refresh</button>
          <button
            className="cl-deleted-btn"
            onClick={() => setShowDeleted(v => !v)}
          >
            {showDeleted ? "📋 Show Active" : `🗑 Deleted (${deleted.length})`}
          </button>
          <button className="cl-minus-btn" onClick={() => setShowMinusModal(true)}>
            − Minus Entry
          </button>
          <button className="dp-add-btn" onClick={() => setShowForm(v => !v)}>
            {showForm ? "✕ Close" : "+ Add Entry"}
          </button>
        </div>
      </div>

      {/* Add Entry Form */}
      {showForm && (
        <div className="cl-add-form">
          <h3 className="cl-form-title">New Collection Entry</h3>
          <div className="cl-form-grid">
            <div className="cl-form-field">
              <label>Amount (₹) *</label>
              <input
                type="number"
                placeholder="Enter amount"
                value={form.amount}
                onChange={e => setForm(p => ({...p, amount: e.target.value}))}
              />
            </div>
            <div className="cl-form-field">
              <label>Type *</label>
              <select
                value={form.type}
                onChange={e => setForm(p => ({...p, type: e.target.value, customSubType: ""}))}
              >
                <option value="">Select type</option>
                {COLLECTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Custom sub-type name input — only shown when Custom is selected */}
            {form.type === "Custom" && (
              <div className="cl-form-field">
                <label>Custom Type Name *</label>
                <input
                  placeholder="e.g. Goat Sacrifice, Eid Collection…"
                  value={form.customSubType}
                  onChange={e => setForm(p => ({...p, customSubType: e.target.value}))}
                />
              </div>
            )}

            <div className="cl-form-field">
              <label>Collected By</label>
              <select
                value={form.bookedBy}
                onChange={e => setForm(p => ({...p, bookedBy: e.target.value}))}
              >
                <option value="">Select volunteer</option>
                {VOLUNTEERS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="cl-form-field">
              <label>Notes</label>
              <input
                placeholder="Optional notes"
                value={form.notes}
                onChange={e => setForm(p => ({...p, notes: e.target.value}))}
              />
            </div>
            <div className="cl-form-field">
              <label style={{display:"flex",alignItems:"center",gap:"0.4rem"}}>
                <input
                  type="checkbox"
                  checked={form.useCustomDate}
                  onChange={e => setForm(p => ({...p, useCustomDate: e.target.checked}))}
                />
                Custom Date
              </label>
              {form.useCustomDate && (
                <input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(p => ({...p, date: e.target.value}))}
                />
              )}
              {!form.useCustomDate && <span className="cl-date-note">Using today's date</span>}
            </div>
          </div>
          <div className="cl-form-actions">
            <button className="dp-cancel-btn" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="dp-save-btn" onClick={handleSubmit}>Add Entry</button>
          </div>
        </div>
      )}

      {/* ── Deleted Records View ── */}
      {showDeleted ? (
        <div className="cl-deleted-section">
          <h3 className="cl-deleted-title">🗑 Deleted Records ({deleted.length})</h3>
          {deleted.length === 0 ? (
            <div className="dp-empty"><span>📭</span><p>No deleted records.</p></div>
          ) : (
            <div className="dp-list">
              {deleted.map(d => (
                <div key={d.id} className="dp-card cl-deleted-card">
                  <div className="dp-card-header" style={{cursor:"default"}}>
                    <div className="dp-card-left">
                      <span className="cl-amount-pill cl-deleted-pill">
                        ₹{parseFloat(d.amount||0).toLocaleString("en-IN")}
                      </span>
                      <span className="dp-card-name">{getEffectiveType(d) || "—"}</span>
                      <span className="dp-card-sub">
                        Deleted {fmt(d.deletedAt)} · Recipient: {d.deletionRecipient || "—"}
                      </span>
                    </div>
                  </div>
                  {d.deletionNote && (
                    <div className="cl-deletion-note">
                      <strong>Reason:</strong> {d.deletionNote}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Summary Row */}
          <div className="cl-summary-row">
            <div className="cl-total-card">
              <span className="cl-total-label">
                {typeFilter === "all" ? "Total Collected" : typeFilter}
              </span>
              <span className="cl-total-amount">
                ₹{totalAmount.toLocaleString("en-IN")}
              </span>
              <span className="cl-total-sub">{filtered.length} entr{filtered.length !== 1 ? "ies" : "y"}</span>
            </div>
            <div className="cl-type-pills">
              <button
                className={"cl-type-pill" + (typeFilter === "all" ? " cl-type-active" : "")}
                onClick={() => setTypeFilter("all")}
              >All</button>
              {uniqueTypes.map(t => (
                <button
                  key={t}
                  className={"cl-type-pill" + (typeFilter === t ? " cl-type-active" : "")}
                  onClick={() => setTypeFilter(t === typeFilter ? "all" : t)}
                >{t}</button>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="dp-filters">
            <input
              className="dp-search"
              placeholder="Search by type, amount, notes or volunteer…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select className="dp-select" value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
            <span className="dp-count">{filtered.length} records</span>
          </div>

          {/* List */}
          {loading ? (
            <div className="dp-loading"><div className="dp-spinner"/><p>Loading…</p></div>
          ) : filtered.length === 0 ? (
            <div className="dp-empty"><span>📭</span><p>No collection entries found.</p></div>
          ) : (
            <div className="dp-list">
              {filtered.map(col => {
                const isOpen = expanded === col.id;
                const hasHistory = col.history && col.history.length > 0;
                return (
                  <div key={col.id} className={"dp-card" + (isOpen ? " dp-card-open" : "")}>
                    <div className="dp-card-header" onClick={() => setExpanded(isOpen ? null : col.id)}>
                      <div className="dp-card-left">
                        <span className="cl-amount-pill">
                          ₹{parseFloat(col.amount||0).toLocaleString("en-IN")}
                        </span>
                        <span className="dp-card-name">{getEffectiveType(col) || "Unknown"}</span>
                        {hasHistory && (
                          <span className="cl-edited-badge">✏️ edited</span>
                        )}
                        <span className="dp-card-sub">
                          {fmt(col.date)}
                          {col.bookedBy ? ` · ${col.bookedBy}` : ""}
                          {col.notes ? ` · ${col.notes}` : ""}
                        </span>
                      </div>
                      <div className="cl-card-actions-row">
                        <button
                          className="cl-edit-btn"
                          onClick={e => { e.stopPropagation(); setEditTarget(col); }}
                        >✏️</button>
                        <button
                          className="cl-del-btn"
                          onClick={e => { e.stopPropagation(); setDeleteTarget(col); }}
                        >🗑</button>
                        <span className="dp-chevron">{isOpen ? "▲" : "▼"}</span>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="dp-card-body">
                        <div className="dp-info-grid">
                          <div className="dp-info-item">
                            <span className="dp-info-key">Amount</span>
                            <span className="dp-info-val" style={{fontWeight:700,fontSize:"1.05rem",color:"#27ae60"}}>
                              ₹{parseFloat(col.amount||0).toLocaleString("en-IN")}
                            </span>
                          </div>
                          <div className="dp-info-item">
                            <span className="dp-info-key">Type</span>
                            <span className="dp-info-val">{getEffectiveType(col) || "—"}</span>
                          </div>
                          <div className="dp-info-item">
                            <span className="dp-info-key">Date</span>
                            <span className="dp-info-val">{fmt(col.date)}</span>
                          </div>
                          <div className="dp-info-item">
                            <span className="dp-info-key">Collected By</span>
                            <span className="dp-info-val">{col.bookedBy || "—"}</span>
                          </div>
                          {col.notes && (
                            <div className="dp-info-item dp-info-full">
                              <span className="dp-info-key">Notes</span>
                              <span className="dp-info-val">{col.notes}</span>
                            </div>
                          )}
                        </div>

                        {/* Edit History */}
                        {hasHistory && (
                          <div className="cl-history-section">
                            <h4 className="cl-history-title">✏️ Edit History</h4>
                            {col.history.map((h, i) => (
                              <div key={i} className="cl-history-item">
                                <div className="cl-history-row">
                                  <span className="cl-history-change">
                                    ₹{h.prevAmount} → ₹{h.newAmount}
                                  </span>
                                  <span className="cl-history-date">{fmt(h.at)}</span>
                                </div>
                                <div className="cl-history-row">
                                  <span className="cl-history-recipient">
                                    👤 {h.recipient}
                                  </span>
                                </div>
                                {h.editNote && (
                                  <p className="cl-history-note">{h.editNote}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Collection;